import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { formattingTime, pad } from './functions';

interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
}
const TimePicker: React.FC<TimePickerProps> = React.memo(
  ({ value, onChange }) => {
    const [currentTime, setCurrentTime] = useState(formattingTime(value));

    const [changing, setChanging] = useState<'hour' | 'minute'>('hour');

    const handleTime = useCallback(
      (num: number) => {
        if (changing === 'hour') {
          const hour = (num + currentTime.isPm) % 24;
          setCurrentTime(({ minute }) => ({
            hour,
            minute,
            isPm: hour >= 12 ? 12 : 0,
          }));
        } else {
          setCurrentTime((time) => ({
            ...time,
            minute: num,
          }));
        }
      },
      [changing, currentTime]
    );

    useLayoutEffect(() => {
      onChange(pad(currentTime.hour) + ':' + pad(currentTime.minute));
    }, [currentTime]);

    return (
      <div className="absolute top-0 w-full bg-white p-5">
        <div className="flex items-center w-full justify-between gap-2.5 h-[5rem]">
          <div className="flex items-center">
            <div
              className={cx('timepicker-modal-input', {
                focus: changing === 'hour',
              })}
            >
              <input
                type="number"
                value={
                  currentTime.hour % 12 === 0
                    ? '12'
                    : pad(currentTime.hour % 12)
                }
                onFocus={() => setChanging('hour')}
                min={0}
                max={12}
                onChange={(e) => {
                  const hour =
                    parseInt(e.currentTarget.value) + currentTime.isPm;
                  setCurrentTime(({ minute }) => ({
                    hour,
                    minute,
                    isPm: hour >= 12 ? 12 : 0,
                  }));
                }}
              />
            </div>
            <p className="text-[56px] text-black text-center leading-[5rem] w-[28px]">
              :
            </p>
            <div
              className={cx('timepicker-modal-input', {
                focus: changing === 'minute',
              })}
            >
              <input
                type="number"
                value={pad(currentTime.minute % 60)}
                onFocus={() => setChanging('minute')}
                min={0}
                max={59}
                onChange={(e) => {
                  const minute = parseInt(e.currentTarget.value);
                  setCurrentTime((time) => ({
                    ...time,
                    minute,
                  }));
                }}
              />
            </div>
          </div>

          <div className="flex flex-col h-full">
            <p
              onClick={() => {
                if (currentTime.isPm) {
                  setCurrentTime(({ hour, minute }) => ({
                    hour: hour - 12,
                    minute,
                    isPm: 0,
                  }));
                }
              }}
              className={cx(
                'meridiem-picker border-b-0',
                currentTime.isPm ? 'text-gray-500' : 'bg-uta-black text-white'
              )}
            >
              AM
            </p>
            <p
              onClick={() => {
                if (currentTime.isPm === 0) {
                  setCurrentTime(({ hour, minute }) => ({
                    hour: hour + 12,
                    minute,
                    isPm: 12,
                  }));
                }
              }}
              className={cx(
                'meridiem-picker border-t-0',
                currentTime.isPm ? 'bg-uta-black text-white' : 'text-gray-500'
              )}
            >
              PM
            </p>
          </div>
        </div>
        <AnalogClock
          time={currentTime}
          changing={changing}
          onSelect={handleTime}
        />
      </div>
    );
  }
);

TimePicker.displayName = 'TimePicker';

interface AnalogClockPropsT {
  onSelect?: (n: number) => void;
  time: { hour: number; minute: number; isPm: number };
  changing: 'hour' | 'minute';
}

const AnalogClock = React.memo(
  ({ onSelect, time, changing }: AnalogClockPropsT) => {
    const dragging = useRef(false);

    const getTransform = () => {
      const angle =
        ((changing === 'hour' ? time.hour % 12 : time.minute) /
          (changing === 'hour' ? 12 : 60)) *
          360 +
        180;
      return `translate(-50%, 0) rotate(${angle}deg)`;
    };

    const handleTime = useCallback(
      (x: number, y: number) => {
        const angle = Math.atan2(y, x);

        const result = Math.round(
          (angle + Math.PI) / ((2 * Math.PI) / (changing === 'hour' ? 12 : 60))
        );

        if (changing === 'hour') {
          onSelect(result > 3 ? result - 3 : result + 9);
        } else {
          onSelect(result >= 15 ? result - 15 : result + 45);
        }
      },
      [changing, time]
    );

    const onTouchStart = useCallback(
      (e: React.TouchEvent) => {
        dragging.current = true;

        const clockRect = e.currentTarget.getBoundingClientRect();

        const x =
          e.targetTouches[0].clientX - clockRect.left - clockRect.width / 2;
        const y =
          e.targetTouches[0].clientY - clockRect.top - clockRect.height / 2;

        handleTime(x, y);
      },
      [changing]
    );

    const onTouchMove = useCallback(
      (e: React.TouchEvent) => {
        if (!dragging.current) {
          return;
        }

        const clockRect = e.currentTarget.getBoundingClientRect();

        const x =
          e.targetTouches[0].clientX - clockRect.left - clockRect.width / 2;
        const y =
          e.targetTouches[0].clientY - clockRect.top - clockRect.height / 2;

        handleTime(x, y);
      },
      [changing]
    );

    const onTouchEnd = useCallback(() => {
      dragging.current = false;
    }, [changing]);

    const onMouseDown = (e: React.MouseEvent) => {
      dragging.current = true;

      const clockRect = e.currentTarget.getBoundingClientRect();

      const x = e.clientX - clockRect.left - clockRect.width / 2;
      const y = e.clientY - clockRect.top - clockRect.height / 2;

      handleTime(x, y);
    };

    const onMouseMove = (e: React.MouseEvent) => {
      if (!dragging.current) {
        return;
      }

      const clockRect = e.currentTarget.getBoundingClientRect();

      const x = e.clientX - clockRect.left - clockRect.width / 2;
      const y = e.clientY - clockRect.top - clockRect.height / 2;

      handleTime(x, y);
    };

    const onMouseUp = () => {
      dragging.current = false;
    };

    return (
      <div
        className="analog-clock"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        <div className="clock-pointer" style={{ transform: getTransform() }} />
        {Array(12)
          .fill(0)
          .map((_, index: number) => (
            <div
              key={index}
              className="h-1/2 absolute text-center left-1/2 origin-[bottom_center]"
              style={{
                transform: `translate(-50%, 0) rotate(${30 * (index + 1)}deg)`,
              }}
            >
              <div
                className="relative inline-block"
                style={{ transform: `rotate(-${30 * (index + 1)}deg)` }}
              >
                <span
                  className={cx(
                    'text-[16px] leading-10 w-10 rounded-full flex items-center justify-center cursor-pointer select-none',
                    ((index + 1 === time.hour % 12 ||
                      (time.hour % 12) + 11 === index) &&
                      changing === 'hour') ||
                      (index + 1 === time.minute / 5 &&
                        changing === 'minute') ||
                      (time.minute === 0 &&
                        index === 11 &&
                        changing === 'minute')
                      ? 'text-white'
                      : 'text-gray-500'
                  )}
                >
                  {changing === 'minute'
                    ? index === 11
                      ? '00'
                      : pad((index + 1) * 5)
                    : index + 1}
                </span>
              </div>
            </div>
          ))}
      </div>
    );
  }
);
AnalogClock.displayName = 'AnalogClock';

export default TimePicker;
